import { useState, useEffect } from 'react';
import { useSupplierSignUp } from '../api/useSupplierSignUp';
import { CenterContainer } from './CenterContainer';
import { Trans, t } from '@lingui/macro';
import styled from '@emotion/styled';
import { Text, OTP, Button } from 'components';
import { AuthLink } from './AuthLink';
import { AuthButton } from './AuthButton';
import { usePost } from 'lib/api';
import { apiMembers } from 'api/members';
import { useNotification } from 'features/notifications';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { useEvent } from 'lib/segment';
import { useUser } from 'features/auth';

export const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const loginId = searchParams.get('loginId');
  const otpParam = searchParams.get('otp');
  const event = useEvent();
  const { data: userData } = useUser();
  const theme = useTheme();
  const [otpValue, setOtpValue] = useState(otpParam || '');
  const { signupResponse, verifyOTP, isVerifying, clearSignupResponse } = useSupplierSignUp(
    loginId || undefined
  );
  const { notify } = useNotification();
  const navigate = useNavigate();
  const finalLoginId = loginId || signupResponse?.id;

  useEffect(() => {
    if (!finalLoginId) {
      clearSignupResponse();
      navigate(`/supplier/signup`, { replace: true });
    }
  }, [signupResponse?.id, navigate]);

  const { mutate: resendOTP, isPending } = usePost<{
    action: 'resend' | 'verify';
    otp?: string;
    id: string;
  }>(apiMembers.post.verifyOTP, false, [loginId || signupResponse?.id || '0'], {
    onSuccess(data) {
      const res = data as { code: string };
      if (res.code === 'signup_otp_sent') {
        event('Supplier OTP Resent', {
          objectType: 'Email',
          action: 'Create',
          targetType: 'OTP',
          targetId: userData?.loginContext?.inviteCode,
          targetName: 'OTP Resend',
          source: 'Button',
          page: 'Email Verification',
          businessContext: {
            email: signupResponse?.email,
          },
        });
        notify.info({ message: res.code, id: 'otp-sent', isCode: true });
      }
    },
  });

  const resend = () => {
    resendOTP({ data: { action: 'resend' } });
  };

  const onOTP = (code: string) => {
    setOtpValue(code);
    if (finalLoginId) {
      verifyOTP({
        data: {
          action: 'verify',
          otp: code,
          id: finalLoginId,
        },
      });
      return;
    }
    notify.error({
      id: 'no-loginid',
      message: t`User id is missing, please restart the sign up process or click the OTP link in your email so we can identify you.`,
      actions: [
        {
          id: 'restart',
          label: t`Sign up`,
          onClick: () => navigate('/supplier/signup', { replace: true }),
        },
      ],
    });
  };

  const back = () => {
    clearSignupResponse();
    navigate('/supplier/signup', { replace: true });
  };

  return (
    <CenterContainer
      welcomeText={
        <Trans>PLM designed for Fashion & Lifestyle developed by actual buyers and designers</Trans>
      }
    >
      <form
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
          if (otpValue) onOTP(otpValue);
        }}
      >
        <FormContainer>
          <Text variant="headlineXs" weight="semiBold" align="center">
            <Trans>Verify email</Trans>
          </Text>
          <Text align="center" color={theme.palette.text.secondary}>
            <Trans>Please type the code you received on email</Trans>
          </Text>
          <OTP
            onComplete={code => onOTP(code)}
            onIncomplete={() => setOtpValue('')}
            initialValue={otpParam || undefined}
          />
          <AuthLink
            onClick={() => resend()}
            questionText={isPending ? '\u200B' : t`Didn't receive anything?`}
            linkText={isPending ? '' : t`Resend the code`}
          />
        </FormContainer>
        <Buttons>
          <div>
            <Button size="lg" type="button" variant="secondary" onClick={() => back()}>
              <Trans>Back</Trans>
            </Button>
          </div>
          <AuthButton disabled={!otpValue} isLoading={isVerifying} buttonText={t`Continue`} />
        </Buttons>
      </form>
    </CenterContainer>
  );
};

const FormContainer = styled.div`
  margin: 24px;
  margin-bottom: 16px;
  gap: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  svg {
    fill: ${p => p.theme.palette.fg.quinary};
  }
`;

const Buttons = styled.div`
  margin: 24px;
  display: flex;
  flex-direction: row;
  gap: 12px;
  > div {
    margin: 0;
    flex: 1;
    > button {
      width: 100%;
    }
  }
`;
